import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { useVMContext } from '../context/VMContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Clock, Play, Square, CheckCircle, AlertCircle, Loader2, Lightbulb, Eye, EyeOff } from 'lucide-react';

// ─── Course Loading Config ─────────────────────────────────────────────────
// Add a new course here for a rich loading experience.
// If a course is NOT listed, the smart fallback below auto-generates content.
// Exam courses (isExamCourse: true) are excluded — they keep their own flow.
const COURSE_LOADING_CONFIG = {
  RH124: {
    headline: 'Starting RHEL System',
    subtitle: 'Your Linux VM is booting up',
    steps: ['Allocating VM', 'Starting system services', 'Mounting filesystems', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ whoami',
      'student',
      '[student@lab ~]$ ls /etc/ | head -5',
      'adjtime  bash.bashrc  cron.d  fstab  hostname',
      '[student@lab ~]$ systemctl status sshd',
      '● sshd.service - OpenSSH Server Daemon',
      '   Active: active (running)',
    ],
    tip: 'Use Tab for auto-complete. Use !! to repeat your last command.',
  },
  RH134: {
    headline: 'Starting Advanced RHEL System',
    subtitle: 'Loading system administration tools',
    steps: ['Allocating VM', 'Loading SELinux policy', 'Starting network stack', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ semanage port -l | head -3',
      'SELinux Port Type         Proto  Port',
      'http_port_t               tcp    80, 443',
      '[student@lab ~]$ firewall-cmd --list-all',
      'public (active)',
      '  services: dhcpv6-client ssh',
    ],
    tip: 'Run httpd -t before every restart to catch config errors early.',
  },
  SVRADM: {
    headline: 'Starting Server Administration Lab',
    subtitle: 'Web and network services loading',
    steps: ['Allocating VM', 'Starting system services', 'Loading web server', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ systemctl list-units --state=running | head -4',
      'UNIT                   LOAD   ACTIVE  DESCRIPTION',
      'sshd.service           loaded active  OpenSSH Server',
      '[student@lab ~]$ ss -tlnp | head -3',
      'State   Local Address:Port   Process',
      'LISTEN  0.0.0.0:22          sshd',
    ],
    tip: 'Use journalctl -xe to debug service failures in real time.',
  },
  DOCKER: {
    headline: 'Starting Docker Lab',
    subtitle: 'Container runtime is loading',
    steps: ['Allocating VM', 'Starting Docker daemon', 'Loading container runtime', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ docker --version',
      'Docker version 24.0.5, build ced0996',
      '[student@lab ~]$ docker ps',
      'CONTAINER ID   IMAGE   STATUS   PORTS   NAMES',
      '[student@lab ~]$ docker images',
      'REPOSITORY   TAG       IMAGE ID   SIZE',
    ],
    tip: 'Use Ctrl+P then Ctrl+Q to detach from a container without stopping it.',
  },
  KUBERNETES: {
    headline: 'Starting Kubernetes Lab',
    subtitle: 'Cluster services are initializing',
    steps: ['Allocating VM', 'Starting kubelet', 'Loading cluster config', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ kubectl get nodes',
      'NAME       STATUS   ROLES           AGE   VERSION',
      'lab-node   Ready    control-plane   1d    v1.28.0',
      '[student@lab ~]$ kubectl get pods -A',
      'NAMESPACE     NAME              READY   STATUS',
      'kube-system   coredns-5d78c     1/1     Running',
    ],
    tip: 'Use kubectl explain <resource> to read inline API docs without googling.',
  },
  ANSIBLE: {
    headline: 'Starting Ansible Lab',
    subtitle: 'Automation environment is ready',
    steps: ['Allocating VM', 'Configuring SSH keys', 'Loading inventory', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ ansible --version',
      'ansible [core 2.15.0]',
      '[student@lab ~]$ ansible all -m ping',
      'localhost | SUCCESS => {',
      '    "ping": "pong"',
      '}',
    ],
    tip: 'Always run ansible-playbook --check before applying to production.',
  },
  TERRAFORM: {
    headline: 'Starting Terraform Lab',
    subtitle: 'Infrastructure tools are loading',
    steps: ['Allocating VM', 'Loading provider plugins', 'Initializing state', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ terraform --version',
      'Terraform v1.6.0',
      '[student@lab ~]$ terraform init',
      'Initializing the backend...',
      'Initializing provider plugins...',
      '✓ Terraform has been successfully initialized!',
    ],
    tip: 'Run terraform fmt to auto-format your .tf files before every commit.',
  },
  GIT: {
    headline: 'Starting Git Lab',
    subtitle: 'Version control tools are ready',
    steps: ['Allocating VM', 'Configuring Git', 'Loading repositories', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ git --version',
      'git version 2.41.0',
      '[student@lab ~]$ git log --oneline -3',
      'a1b2c3d  Initial commit',
      'e4f5g6h  Add README',
      '[student@lab ~]$ git status',
      'On branch main — nothing to commit',
    ],
    tip: 'Use git stash before switching branches to save uncommitted work.',
  },
  PYTHON: {
    headline: 'Starting Python Lab',
    subtitle: 'Python runtime is loading',
    steps: ['Allocating VM', 'Loading Python runtime', 'Setting up workspace', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ python3 --version',
      'Python 3.11.0',
      '[student@lab ~]$ python3 -c "print(\'Hello, World!\')"',
      'Hello, World!',
      '[student@lab ~]$ pip3 list | head -3',
      'Package    Version',
      'pip        23.0',
    ],
    tip: 'Use python3 -m venv env to create isolated project environments.',
  },
  C_LANG: {
    headline: 'Starting C Programming Lab',
    subtitle: 'GCC compiler is ready',
    steps: ['Allocating VM', 'Loading GCC compiler', 'Setting up workspace', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ gcc --version',
      'gcc (GCC) 11.4.0',
      '[student@lab ~]$ cat hello.c',
      '#include <stdio.h>',
      'int main() { printf("Hello!\\n"); return 0; }',
      '[student@lab ~]$ gcc hello.c -o hello && ./hello',
      'Hello!',
    ],
    tip: 'Always compile with -Wall to catch common warnings early.',
  },
  CPP: {
    headline: 'Starting C++ Lab',
    subtitle: 'G++ compiler is ready',
    steps: ['Allocating VM', 'Loading G++ compiler', 'Setting up workspace', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ g++ --version',
      'g++ (GCC) 11.4.0',
      '[student@lab ~]$ cat hello.cpp',
      '#include <iostream>',
      'int main() { std::cout << "Hello!" << std::endl; }',
      '[student@lab ~]$ g++ hello.cpp -o hello && ./hello',
      'Hello!',
    ],
    tip: 'Use -std=c++17 flag for access to modern C++ features.',
  },
  MYSQL: {
    headline: 'Starting MySQL Lab',
    subtitle: 'Database server is starting',
    steps: ['Allocating VM', 'Starting MySQL server', 'Loading databases', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ mysql --version',
      'mysql  Ver 8.0.33',
      '[student@lab ~]$ mysql -u root -p',
      'Welcome to the MySQL monitor.',
      'mysql> SHOW DATABASES;',
      '+--------------------+',
      '| information_schema |',
    ],
    tip: 'Use EXPLAIN before SELECT to see how MySQL executes your query.',
  },
  JAVA: {
    headline: 'Starting Java Lab',
    subtitle: 'JDK environment is loading',
    steps: ['Allocating VM', 'Loading JDK', 'Setting up workspace', 'Opening terminal'],
    terminalLines: [
      '[student@lab ~]$ java --version',
      'openjdk 17.0.8 2023-07-18',
      '[student@lab ~]$ javac Hello.java && java Hello',
      'Hello, World!',
      '[student@lab ~]$ jar --version',
      'jar 17.0.8',
    ],
    tip: 'Use javac -verbose to see exactly what the compiler is doing.',
  },
};

// Smart fallback — auto-generates reasonable content for any new course
// No manual update needed when a new course is added to courseData.js
const getLoadingFallback = (courseName) => ({
  headline: `Starting ${courseName} Lab`,
  subtitle: 'Your Linux VM is booting up',
  steps: ['Allocating VM', 'Configuring environment', 'Loading tools', 'Opening terminal'],
  terminalLines: [
    '[student@lab ~]$ whoami',
    'student',
    '[student@lab ~]$ pwd',
    '/home/student',
    '[student@lab ~]$ ls',
    'Desktop  Documents  Downloads  labs',
  ],
  tip: 'Use Tab for auto-complete and Ctrl+R to search your command history.',
});

// Maps vmProgress (0-100) to step status for 4 steps
const getStepStatus = (stepIndex, progress) => {
  const done = (stepIndex + 1) * 25;
  const active = stepIndex * 25;
  if (progress >= done) return 'done';
  if (progress >= active) return 'active';
  return 'pending';
};
// ────────────────────────────────────────────────────────────────────────────

// ─── Hint Toggle Component ───────────────────────────
const HintToggle = ({ hint, taskIndex }) => {
  const [stage, setStage] = React.useState('hidden'); // hidden | warning | revealed

  if (stage === 'hidden') {
    return (
      <button
        onClick={() => setStage('warning')}
        className="mb-4 flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded-lg transition-all duration-200"
        data-testid={`show-hint-btn-${taskIndex + 1}`}
      >
        <Lightbulb className="h-3.5 w-3.5" />
        Show Hint
      </button>
    );
  }

  if (stage === 'warning') {
    return (
      <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg" data-testid={`hint-warning-${taskIndex + 1}`}>
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm font-bold text-amber-800">Try yourself first!</span>
        </div>
        <p className="text-xs text-amber-700 mb-3 leading-relaxed">
          Solving on your own builds real skills. Only peek if you're truly stuck.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setStage('revealed')}
            className="flex items-center gap-1 text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-md transition-colors"
            data-testid={`reveal-hint-btn-${taskIndex + 1}`}
          >
            <Eye className="h-3 w-3" />
            I'm stuck, show me
          </button>
          <button
            onClick={() => setStage('hidden')}
            className="text-xs font-medium text-amber-600 hover:text-amber-800 px-3 py-1.5 rounded-md hover:bg-amber-100 transition-colors"
          >
            I'll try more
          </button>
        </div>
      </div>
    );
  }

  // stage === 'revealed'
  return (
    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`hint-revealed-${taskIndex + 1}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
          <span className="text-xs font-bold text-amber-700">Hint</span>
        </div>
        <button
          onClick={() => setStage('hidden')}
          className="flex items-center gap-1 text-xs text-amber-500 hover:text-amber-700 transition-colors"
          data-testid={`hide-hint-btn-${taskIndex + 1}`}
        >
          <EyeOff className="h-3 w-3" />
          Hide
        </button>
      </div>
      <code className="block whitespace-pre-wrap break-words overflow-x-auto text-xs text-gray-800 bg-white/70 p-2 rounded border border-amber-100 font-mono leading-relaxed" style={{ overflowWrap: 'anywhere' }}>
        {hint}
      </code>
    </div>
  );
};
// ─────────────────────────────────────────────────────
import { toast } from 'sonner';
import { COURSE_DATA } from '../constants/courseData';

// Motivational messages for failed tasks
const motivationMessages = [
  "💪 Don't give up! Every expert was once a beginner",
  "🚀 Try again! You're closer than you think",
  "⭐ Keep going! Great things take time", 
  "🎯 Almost there! One more attempt",
  "🔥 Stay focused! Success is just around the corner",
  "💡 Learn from mistakes and try again",
  "🌟 Believe in yourself! You can do it",
  "⚡ Power through! Champions never quit",
  "🎖️ Every failure is a step towards success",
  "🚀 Rocket fuel your determination and retry!"
];

// Success celebration messages
const successMessages = [
  "🎉 Outstanding! You're a command line hero!",
  "🏆 Brilliant work! Linux mastery level up!",
  "⭐ Excellent! You've conquered this challenge!",
  "🚀 Amazing! Your terminal skills are rocket-powered!",
  "💎 Perfect execution! You're a Linux gem!",
  "🎯 Bullseye! Direct hit on success!",
  "🔥 On fire! Keep burning bright!",
  "⚡ Lightning fast! Superb terminal work!",
  "🌟 Stellar performance! You're shining!",
  "👑 Crowned with success! Royal achievement!"
];

const Labs = () => {
  const { course, labId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const vmContext = useVMContext();
  const [loading, setLoading] = useState(true);
  const [lab, setLab] = useState(null);
  const [terminalStatus, setTerminalStatus] = useState({ running: false, url: null, container_id: null });
  const [results, setResults] = useState({});
  const [verifying, setVerifying] = useState({});
  const [progress, setProgress] = useState({ completed_tasks: [], score: 0 });
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(null);
  const [motivationMessage, setMotivationMessage] = useState('');
  const [error, setError] = useState('');
  const [terminalSession, setTerminalSession] = useState(null);
  const [terminalLoading, setTerminalLoading] = useState(false);
  const [vmProgress, setVmProgress] = useState(0);
  const [animLineIndex, setAnimLineIndex] = useState(0);
  const animTimerRef = useRef(null);
  const [markingComplete, setMarkingComplete] = useState({});

  // Check if this is a cloud course (self-verification)
  const isCloudCourse = COURSE_DATA[course]?.isCloudCourse || false;

  // Get course info from COURSE_DATA (single source of truth)
  const currentCourseData = COURSE_DATA[course] || {};
  const courseUIConfig = {
    name: currentCourseData.name || course,
    icon: currentCourseData.icon || '🖥️',
    logo: currentCourseData.logo || null,
    color: currentCourseData.color || 'gray',
    type: currentCourseData.isProgrammingCourse ? 'Programming' : 
          currentCourseData.isExamCourse ? 'Exam Practice' : 
          currentCourseData.level || 'Practice',
    env: 'Linux Virtual Machine'
  };
  
  // Get self-verify tasks for hybrid courses (like TERRAFORM)
  const selfVerifyTasks = COURSE_DATA[course]?.selfVerifyTasks || {};
  
  // Check if a specific task should use self-verification (Mark Complete)
  const isSelfVerifyTask = (labId, taskIndex) => {
    const taskId = `q${taskIndex + 1}`;
    const labKey = labId; // e.g., "lab1"
    return selfVerifyTasks[labKey]?.includes(taskId) || false;
  };

  useEffect(() => {
    fetchLabData();
    setTimeout(() => {
      checkTerminalStatus();
    }, 1500);
  }, [course, labId]);

  // Poll for terminal status when terminal is starting but URL is not ready
  useEffect(() => {
    let pollInterval;
    
    if (terminalStatus.running && !terminalStatus.url && !terminalLoading) {
      pollInterval = setInterval(async () => {
        try {
          const response = await apiService.getTerminalStatus(course, labId);
          const statusData = response.data || response;
          
          if (statusData && statusData.running && statusData.terminalUrl) {
            setTerminalStatus({
              running: true,
              url: statusData.terminalUrl,
              container_id: statusData.vm_id || statusData.container_id
            });
            
            setTerminalSession({
              terminalUrl: statusData.terminalUrl,
              container: statusData.vm_id || statusData.container_id,
              port: statusData.port,
              ttlMinutes: statusData.ttlMinutes,
              reuse: statusData.reuse || false
            });
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 3000); // Poll every 3 seconds
    }
    
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [terminalStatus.running, terminalStatus.url, terminalLoading, course, labId]);

  // Heartbeat with user activity detection - only sends when user is active
  useEffect(() => {
    let heartbeatInterval;
    let lastActivityTime = Date.now();
    const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes idle = stop heartbeat
    const HEARTBEAT_INTERVAL = 2 * 60 * 1000; // 2 minutes
    
    // Track user activity
    const updateActivity = () => {
      lastActivityTime = Date.now();
    };
    
    if (terminalStatus.running && terminalStatus.url) {
      // Add activity listeners
      const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, updateActivity, { passive: true });
      });
      
      // Send heartbeat immediately when terminal becomes active
      apiService.sendHeartbeat(course, labId).catch(() => {});
      
      // Send heartbeat every 2 minutes, but only if user was active in last 15 min
      heartbeatInterval = setInterval(async () => {
        const idleTime = Date.now() - lastActivityTime;
        
        if (idleTime < IDLE_TIMEOUT) {
          try {
            await apiService.sendHeartbeat(course, labId);
          } catch (error) {
            // heartbeat failure is non-critical
          }
        } else {
          // user idle, skip heartbeat
        }
      }, HEARTBEAT_INTERVAL);
      
      return () => {
        if (heartbeatInterval) {
          clearInterval(heartbeatInterval);
        }
        // Remove activity listeners
        events.forEach(event => {
          document.removeEventListener(event, updateActivity);
        });
      };
    }
    
    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [terminalStatus.running, terminalStatus.url, course, labId]);

  // Terminal loading animation — advances one line every 600ms
  useEffect(() => {
    if (terminalLoading) {
      setAnimLineIndex(0);
      animTimerRef.current = setInterval(() => {
        setAnimLineIndex(prev => prev + 1);
      }, 600);
    } else {
      clearInterval(animTimerRef.current);
    }
    return () => clearInterval(animTimerRef.current);
  }, [terminalLoading]);

  const fetchLabData = async () => {
    try {
      // Check lab access first
      const FREE_LABS = {
        'RH124': ['lab1', 'lab2', 'lab3'],
        'C_LANG': ['lab1', 'lab2'],
        'PYTHON': [],
        'CPP': []
      };
      
      const isFreeLab = FREE_LABS[course]?.includes(labId) || false;
      
      // Check if user has premium access
      let isPremiumUser = false;
      try {
        const accessResponse = await apiService.getCourseAccess(course);
        const accessData = accessResponse.data || accessResponse;
        isPremiumUser = accessData?.user_access?.access_type === 'premium';
      } catch (accessError) {
        console.log('Access check failed, defaulting to free access only');
      }
      
      // If not free lab and not premium user, redirect to course page
      if (!isFreeLab && !isPremiumUser) {
        toast.error('🔒 This is a premium lab. Please purchase the course to access.');
        navigate(`/course/${course}`);
        return;
      }
      
      // Fetch lab detail first (required)
      const labResponse = await apiService.getLabDetail(course, labId);
      
      // Handle direct response vs nested data structure
      const labData = labResponse.data || labResponse;
      setLab(labData);
      
      // Fetch progress separately (optional - don't fail if this errors)
      let progressData = [];
      try {
        const progressResponse = await apiService.getMyProgress();
        progressData = progressResponse.data || progressResponse;
      } catch (progressError) {
        // Non-critical - continue without progress data
      }
      
      // Handle progress data as array or direct object
      const userProgress = Array.isArray(progressData) 
        ? progressData.find(p => p.long_id === `${course}_${labId}`)
        : progressData.long_id === `${course}_${labId}` ? progressData : null;
        
      if (userProgress) {
        // Ensure completed_tasks is always an array
        const completedTasks = Array.isArray(userProgress.completed_tasks) 
          ? userProgress.completed_tasks 
          : [];
          
        setProgress({
          ...userProgress,
          completed_tasks: completedTasks,
          score: userProgress.score || 0
        });
        
        // Set initial results based on completed tasks
        const initialResults = {};
        completedTasks.forEach(taskId => {
          initialResults[`task${taskId.replace('q', '')}`] = { passed: true };
        });
        setResults(initialResults);
      } else {
        // Set default progress if no user progress found
        setProgress({
          completed_tasks: [],
          score: 0
        });
      }
    } catch (error) {
      toast.error(`Failed to load lab data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const checkTerminalStatus = async () => {
    try {
      // Try the terminal status API first  
      const response = await apiService.getTerminalStatus(course, labId);
      const statusData = response.data || response;
      
      if (statusData && statusData.running && statusData.terminalUrl) {
        // Extract port from terminalUrl for VM sessions
        let port = statusData.port;
        if (!port && statusData.terminalUrl) {
          const urlMatch = statusData.terminalUrl.match(/:(\d+)$/);
          port = urlMatch ? parseInt(urlMatch[1]) : 30000;
        }
        
        setTerminalStatus({
          running: true,
          url: statusData.terminalUrl,
          container_id: statusData.vm_id || statusData.container_id
        });
        
        setTerminalSession({
          terminalUrl: statusData.terminalUrl,
          container: statusData.vm_id || statusData.container_id,
          port: port,
          ttlMinutes: statusData.ttlMinutes,
          reuse: statusData.reuse || false
        });
        
        vmContext.setSession({
          course: course,
          lab_id: labId,
          container_id: statusData.container_id,
          terminalUrl: statusData.terminalUrl,
          ttlMinutes: 30,
        });
        return;
      }
    } catch (error) {
      // fall through to legacy path
    }

    try {
      // Fallback to legacy terminal status API
      const response = await apiService.getTerminalStatus(course, labId);
      
      const statusData = response.data || response;
      
      if (statusData && statusData.running && statusData.terminalUrl) {
        // Properly restore terminal session with slight delay to ensure state consistency
        setTimeout(() => {
          setTerminalStatus({
            running: true,
            url: statusData.terminalUrl,
            container_id: statusData.container_id
          });
          
          setTerminalSession({
            terminalUrl: statusData.terminalUrl,
            container: statusData.container_id,
            port: statusData.port,
            ttlMinutes: statusData.ttlMinutes,
            reuse: statusData.reuse
          });
          vmContext.setSession({
            course: course,
            lab_id: labId,
            container_id: statusData.container_id,
            terminalUrl: statusData.terminalUrl,
            ttlMinutes: 30,
          });
        }, 100);
      } else {
        setTerminalStatus({
          running: false,
          url: null,
          container_id: null
        });
      }
    } catch (error) {
      setTerminalStatus({
        running: false,
        url: null,
        container_id: null
      });
    }
  };

  const handleStartTerminal = async () => {
    setTerminalLoading(true);
    setVmProgress(0);
    
    // Scroll terminal card into view so user sees the progress
    setTimeout(() => {
      terminalCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    
    try {
      toast.info('🚀 Starting your personal Linux VM... Get ready for adventure!');
      
      // Simulate progress updates during VM creation
      const progressInterval = setInterval(() => {
        setVmProgress(prev => {
          if (prev < 85) {
            return prev + Math.random() * 15;
          }
          return prev;
        });
      }, 800);
      
      const response = await apiService.startTerminal(course, labId);
      clearInterval(progressInterval);
      setVmProgress(100);
      
      // Handle response structure properly
      const terminalData = response.data || response;
      
      setTerminalStatus({
        running: true,
        url: terminalData.terminalUrl,
        container_id: terminalData.container_id || terminalData.vm_id
      });
      
      setTerminalSession({
        terminalUrl: terminalData.terminalUrl,
        container: terminalData.container_id || terminalData.vm_id,
        port: terminalData.port,
        ttlMinutes: terminalData.ttlMinutes,
        reuse: terminalData.reused || false
      });
      
      const reusedMsg = terminalData.reused ? 'reconnected' : 'launched';
      toast.success(`🚀 Welcome Command Line Hero! Terminal ${reusedMsg} successfully!`);
      vmContext.setSession({
        course: course,
        lab_id: labId,
        container_id: terminalData.container_id || terminalData.vm_id,
        terminalUrl: terminalData.terminalUrl,
        ttlMinutes: terminalData.ttlMinutes || 30,
      });
    } catch (error) {
      console.error('Failed to start terminal:', error);
      toast.error(`Failed to start terminal: ${error.message}`);
    } finally {
      setTerminalLoading(false);
      setVmProgress(0);
    }
  };

  const [stoppingTerminal, setStoppingTerminal] = useState(false);
  const terminalCardRef = useRef(null);

  const handleStopTerminal = async () => {
    setStoppingTerminal(true);
    try {
      await apiService.stopTerminal(course, labId);
      setTerminalStatus({ running: false, url: null, container_id: null });
      toast.success('Terminal stopped successfully');
      vmContext.clearSession();
    } catch (error) {
      console.error('Failed to stop terminal:', error);
      toast.error(`Failed to stop terminal: ${error.message}`);
    } finally {
      setStoppingTerminal(false);
    }
  };

  const getRandomMessage = (messageArray) => {
    return messageArray[Math.floor(Math.random() * messageArray.length)];
  };

  const triggerSuccessAnimation = (taskId) => {
    setShowSuccessAnimation(taskId);
    setTimeout(() => setShowSuccessAnimation(null), 3000);
  };

  const handleVerifyTask = async (taskId) => {
    setVerifying(prev => ({ ...prev, [taskId]: true }));
    
    try {
      const result = await apiService.verifyTask(course, labId, taskId);
      
      setResults(prev => ({
        ...prev,
        [taskId]: result
      }));
      
      if (result.passed === true) {
        const message = getRandomMessage(successMessages);
        toast.success(message);
        triggerSuccessAnimation(taskId);
        
        // Update progress
        setProgress(prev => ({
          ...prev,
          completed_tasks: [...new Set([...prev.completed_tasks, taskId.replace('task', 'q')])],
          score: prev.score + result.earned
        }));
      } else if (result.passed === false) {
        const motivation = getRandomMessage(motivationMessages);
        setMotivationMessage(motivation);
        const errorMsg = result.results?.[0]?.message || result.message || 'Task failed';
        toast.error(`${taskId}: ${errorMsg}`);
        
        // Show motivation message
        setTimeout(() => {
          toast.info(motivation, { autoClose: 4000 });
        }, 1000);
      } else {
        toast.error(`Task ${taskId} error: ${result.message || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error('Verification failed:', error);
      toast.error(`Failed to verify task: ${error.message}`);
    } finally {
      setVerifying(prev => ({ ...prev, [taskId]: false }));
    }
  };

  // Handle self-marking completion for cloud courses (Terraform)
  const handleMarkComplete = async (taskId, taskIndex, taskPoints) => {
    setMarkingComplete(prev => ({ ...prev, [taskId]: true }));
    
    try {
      // Call API to mark task as complete (self-reported)
      const result = await apiService.markTaskComplete(course, labId, taskId);
      
      const message = getRandomMessage(successMessages);
      toast.success(message);
      triggerSuccessAnimation(taskId);
      
      // Update progress
      setProgress(prev => ({
        ...prev,
        completed_tasks: [...new Set([...prev.completed_tasks, `q${taskIndex + 1}`])],
        score: prev.score + (taskPoints || 10)
      }));
      
      setResults(prev => ({
        ...prev,
        [taskId]: { passed: true }
      }));
      
    } catch (error) {
      console.error('Mark complete failed:', error);
      toast.error(`Failed to mark task complete: ${error.message}`);
    } finally {
      setMarkingComplete(prev => ({ ...prev, [taskId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-400 border-solid mx-auto mb-4"></div>
          <p className="text-white text-xl font-semibold animate-pulse">Loading Lab Environment...</p>
        </div>
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 to-red-700 flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-3xl font-bold mb-4">⚠️ Lab Not Found</h2>
          <p className="text-xl mb-6">The requested lab could not be found.</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="bg-white text-red-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="border-b border-indigo-200 bg-white/80 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  // Smart navigation - check if user came from a specific page
                  try {
                    const savedContext = localStorage.getItem('coursepage_context');
                    if (savedContext) {
                      const context = JSON.parse(savedContext);
                      const isRecent = Date.now() - context.timestamp < 3600000; // 1 hour
                      
                      if (isRecent && context.course === course) {
                        // Navigate back with page context preserved
                        navigate(`/courses/${course}#page-${context.page}`);
                        return;
                      }
                    }
                  } catch (error) {
                    console.log('Navigation context error:', error);
                  }
                  
                  // Default navigation to course page
                  navigate(`/courses/${course}`);
                }}
                className="text-indigo-600 hover:text-indigo-800 transition-colors flex items-center space-x-2 font-medium"
              >
                <span>←</span>
                <span>{course}</span>
              </button>
              <div className="text-gray-400">|</div>
              <span className="text-gray-900 font-semibold">{labId}</span>
            </div>
            <div className="text-right">
              <div className="text-indigo-600 text-sm font-medium">{courseUIConfig.name} Lab Environment</div>
              <div className="text-gray-900 font-bold text-lg">{lab?.title}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]" data-grid-container>
          {/* Left Panel - Lab Tasks */}
          <Card className="lg:col-span-1 overflow-hidden" data-tasks-panel>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Lab Tasks</CardTitle>
                <span className="text-sm text-gray-500">
                  {Array.isArray(progress.completed_tasks) ? progress.completed_tasks.length : 0}/{lab?.tasks?.length || 0} Complete
                </span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${Math.round(((Array.isArray(progress.completed_tasks) ? progress.completed_tasks.length : 0) / (lab?.tasks?.length || 1)) * 100)}%` 
                  }}
                ></div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Progress: {Math.round(((Array.isArray(progress.completed_tasks) ? progress.completed_tasks.length : 0) / (lab?.tasks?.length || 1)) * 100)}%
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0 h-full flex flex-col">
              {/* Tasks List - Clean & Simple with Fixed Spacing */}
              <div className="flex-1 overflow-y-auto space-y-4 pr-2 custom-scrollbar pb-6" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                {lab?.tasks?.map((task, index) => {
                  const taskId = `task${index + 1}`;
                  const completedTasks = Array.isArray(progress.completed_tasks) ? progress.completed_tasks : [];
                  const isCompleted = completedTasks.includes(`q${index + 1}`);
                  const isVerifying = verifying[taskId];
                  const hasSuccessAnimation = showSuccessAnimation === taskId;
                  
                  return (
                    <div key={taskId} className={`p-4 border-2 rounded-xl transition-all duration-300 overflow-hidden ${
                      index === (lab?.tasks?.length || 0) - 1 ? 'mb-6' : ''
                    } ${
                      isCompleted 
                        ? 'bg-gradient-to-br from-green-100 via-emerald-50 to-green-100 border-green-400 shadow-lg transform scale-105 ring-2 ring-green-300 ring-opacity-50' 
                        : isVerifying
                        ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-400 shadow-lg animate-pulse-glow'
                        : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-blue-300 hover:shadow-sm'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {isCompleted ? (
                            <div className="relative">
                              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg animate-pulse">
                                ✓
                              </div>
                              <div className="absolute -top-1 -right-1 text-sm animate-bounce">🎉</div>
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                              <div className="text-sm font-bold text-white">{index + 1}</div>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Task Badge + Title */}
                          <div className="flex items-start gap-2 mb-3">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-bold flex-shrink-0">
                              Task {index + 1}
                            </span>
                            <h4 className={`text-sm font-bold break-words ${
                              isCompleted ? 'text-green-900' : 'text-gray-900'
                            }`} style={{ overflowWrap: 'anywhere' }}>
                              {task.title}
                            </h4>
                          </div>
                          
                          {/* Task Instructions - Collapsible */}
                          {task.instructions && (
                            <details className="mb-4 group">
                              <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                <span className="group-open:rotate-90 transition-transform">▶</span>
                                📋 View Instructions
                              </summary>
                              <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                                <pre className="whitespace-pre-wrap font-mono text-xs text-gray-700 overflow-x-auto">
                                  {task.instructions}
                                </pre>
                              </div>
                            </details>
                          )}
                          
                          {/* Task Hint - Show Hint Toggle with "Try yourself first!" warning */}
                          {task.hint && (
                            <HintToggle hint={task.hint} taskIndex={index} />
                          )}
                          
                          {/* Progress Bar for Individual Task */}
                          <div className="mb-4">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full transition-all duration-500 ${
                                  isCompleted ? 'bg-green-500' : 'bg-blue-300'
                                }`}
                                style={{ width: isCompleted ? '100%' : '0%' }}
                              ></div>
                            </div>
                          </div>
                          
                          {/* Task Actions */}
                          <div className="flex items-center justify-between">
                            <span className={`px-3 py-1 rounded-full font-medium text-xs ${
                              isCompleted 
                                ? 'bg-green-200 text-green-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              💎 {task.points || 10} pts
                            </span>
                            
                            {(isCloudCourse || isSelfVerifyTask(labId, index)) ? (
                              <button
                                onClick={() => handleMarkComplete(taskId, index, task.points)}
                                disabled={markingComplete[taskId] || isCompleted}
                                className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-200 whitespace-nowrap ${
                                  isCompleted
                                    ? 'bg-green-500 text-white cursor-default shadow-lg'
                                    : markingComplete[taskId]
                                    ? 'bg-violet-500 text-white animate-pulse shadow-lg'
                                    : 'bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white hover:scale-105 shadow-lg'
                                }`}
                              >
                                {markingComplete[taskId] ? '⏳ Saving...' : 
                                 isCompleted ? '🎉 Completed!' : '✅ Mark Complete'}
                              </button>
                            ) : terminalStatus.running ? (
                              <button
                                onClick={() => handleVerifyTask(taskId)}
                                disabled={isVerifying || isCompleted}
                                className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-200 whitespace-nowrap ${
                                  isCompleted
                                    ? 'bg-green-500 text-white cursor-default shadow-lg'
                                    : isVerifying
                                    ? 'bg-yellow-500 text-white animate-pulse shadow-lg'
                                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white hover:scale-105 shadow-lg'
                                }`}
                              >
                                {isVerifying ? '⭐ Checking Magic...' : 
                                 isCompleted ? '🎉 Completed!' : '🚀 Verify Task'}
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">
                                🔒 Launch terminal first
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Extra bottom padding */}
                <div className="h-4"></div>
              </div>
            </CardContent>
          </Card>

          {/* Right Panel - Terminal */}
          <Card ref={terminalCardRef} className="lg:col-span-2 overflow-hidden flex flex-col">
            <CardHeader className="pb-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium">Command Line Heroes Hub</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary" className="bg-red-600 text-white">
                    Live Environment
                  </Badge>
                  {terminalStatus.running && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleStopTerminal}
                      disabled={stoppingTerminal}
                      className="text-white border-white hover:bg-red-600"
                    >
                      {stoppingTerminal ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Square className="h-4 w-4 mr-1" />
                      )}
                      {stoppingTerminal ? 'Stopping...' : 'Stop Terminal'}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-0 bg-gray-900 text-white overflow-auto" style={{ height: '500px', minHeight: '500px', maxHeight: '500px' }}>
              {terminalLoading ? (
                (() => {
                  // Exam courses keep their own loading flow — don't touch
                  if (currentCourseData.isExamCourse) {
                    return (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
                        <div className="text-center space-y-3 max-w-lg mx-auto">
                          <div className="text-4xl">🚀</div>
                          <h3 className="text-lg font-bold text-white">Launching Your VM Environment</h3>
                          <p className="text-gray-400 text-xs">Setting up your exam environment...</p>
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-400">Progress</span>
                              <span className="text-blue-400 font-mono">{Math.round(vmProgress)}%</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2">
                              <div className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500" style={{ width: `${Math.min(vmProgress, 100)}%` }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Course loading — rich experience with terminal preview
                  const loadingCfg = COURSE_LOADING_CONFIG[course] || getLoadingFallback(courseUIConfig.name);
                  const visibleLines = loadingCfg.terminalLines.slice(0, animLineIndex);

                  return (
                    <div className="w-full h-full flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-y-auto">
                      <div className="flex-1 p-4 space-y-3 max-w-lg mx-auto w-full">

                        {/* Headline */}
                        <div className="text-center space-y-0.5 pt-2">
                          <div className="flex items-center justify-center space-x-2">
                            <Loader2 className="h-4 w-4 animate-spin text-green-400" />
                            <h3 className="text-base font-bold text-white">{loadingCfg.headline}</h3>
                          </div>
                          <p className="text-gray-400 text-xs">{loadingCfg.subtitle}</p>
                        </div>

                        {/* Fake terminal — course-specific commands animate in */}
                        <div className="bg-black/70 border border-gray-700 rounded-lg overflow-hidden">
                          <div className="flex items-center space-x-1.5 px-3 py-2 bg-gray-800/80 border-b border-gray-700">
                            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
                            <span className="text-gray-500 text-xs ml-1 font-mono">terminal</span>
                          </div>
                          <div className="p-3 font-mono text-xs space-y-0.5 min-h-[100px]">
                            {visibleLines.map((line, i) => (
                              <div
                                key={i}
                                className={line.startsWith('[student@') ? 'text-green-400' : 'text-gray-300'}
                              >
                                {line}
                              </div>
                            ))}
                            {/* Blinking cursor */}
                            <span className="text-green-400 animate-pulse">▋</span>
                          </div>
                        </div>

                        {/* Boot steps — driven by vmProgress */}
                        <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 space-y-2">
                          {loadingCfg.steps.map((step, i) => {
                            const status = getStepStatus(i, vmProgress);
                            return (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <div className="flex items-center space-x-2">
                                  {status === 'done' && <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>}
                                  {status === 'active' && <Loader2 className="w-2 h-2 text-yellow-400 animate-spin flex-shrink-0" />}
                                  {status === 'pending' && <div className="w-2 h-2 bg-gray-600 rounded-full flex-shrink-0"></div>}
                                  <span className={status === 'pending' ? 'text-gray-500' : 'text-gray-300'}>{step}</span>
                                </div>
                                {status === 'done' && <span className="text-green-400">Done</span>}
                                {status === 'active' && <span className="text-yellow-400">In progress</span>}
                                {status === 'pending' && <span className="text-gray-500">Pending</span>}
                              </div>
                            );
                          })}
                        </div>

                        {/* Tip */}
                        <div className="flex items-start space-x-2 bg-blue-900/20 border border-blue-800/30 rounded-lg px-3 py-2">
                          <span className="text-blue-400 text-sm flex-shrink-0">💡</span>
                          <p className="text-blue-200 text-xs leading-relaxed">{loadingCfg.tip}</p>
                        </div>

                        {/* Progress bar */}
                        <div className="space-y-1 pb-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-400">⏱️ 30–60 seconds</span>
                            <span className="text-blue-400 font-mono">{Math.round(vmProgress)}%</span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-1.5">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-green-500 h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(vmProgress, 100)}%` }}
                            ></div>
                          </div>
                        </div>

                      </div>
                    </div>
                  );
                })()

              ) : !terminalStatus.running ? (
                <div className="w-full h-full flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">

                  {/* Scrollable content — works on all screen sizes */}
                  <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center p-5 min-h-0">
                    <div className="text-center w-full max-w-sm space-y-4">

                      {/* Headline */}
                      <div className="space-y-1.5">
                        <div className="text-4xl">🖥️</div>
                        <h3 className="text-lg font-bold text-white leading-snug">
                          Ready to get hands-on?
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          You'll get a real Linux VM — isolated,<br />
                          pre-configured, and ready in under a minute.
                        </p>
                      </div>

                      {/* Feature pills */}
                      <div className="bg-gray-800/50 border border-gray-700/60 rounded-xl p-3.5 space-y-3">
                        <div className="flex items-center justify-center space-x-2 pb-2.5 border-b border-gray-700/60">
                          <span className="text-yellow-400">⚡</span>
                          <span className="text-xs font-semibold text-white tracking-wide">Powered by Grras Solutions</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-300">
                          <div className="flex items-center space-x-2 bg-gray-700/30 rounded-lg px-2.5 py-2">
                            <span>🖥️</span>
                            <span>Real Virtual Machine</span>
                          </div>
                          <div className="flex items-center space-x-2 bg-gray-700/30 rounded-lg px-2.5 py-2">
                            <span>🔒</span>
                            <span>Your private space</span>
                          </div>
                          <div className="flex items-center space-x-2 bg-gray-700/30 rounded-lg px-2.5 py-2">
                            <span>⏱️</span>
                            <span>30-minute session</span>
                          </div>
                          <div className="flex items-center space-x-2 bg-gray-700/30 rounded-lg px-2.5 py-2">
                            <span>🔄</span>
                            <span>Resets after use</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Button always pinned at bottom — never goes off screen */}
                  <div className="px-5 py-4 bg-gray-900/70 border-t border-gray-700/50 shrink-0">
                    <Button
                      onClick={handleStartTerminal}
                      disabled={terminalLoading}
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white py-3 text-sm font-bold shadow-lg rounded-xl transition-all duration-200"
                    >
                      {terminalLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Launching your VM...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Launch My Lab
                        </>
                      )}
                    </Button>
                    <p className="text-center text-gray-500 text-xs mt-2">
                      VM boots in 30–60 seconds
                    </p>
                  </div>

                </div>
              ) : (
                <div className="w-full" style={{ height: '400px' }}>
                  {terminalStatus.url ? (
                    <iframe
                      key={terminalStatus.url}
                      src={terminalStatus.url}
                      className="w-full border-none"
                      style={{ height: '500px' }}
                      title="RHEL Terminal"
                      allow="clipboard-read; clipboard-write"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center space-y-4">
                        <div className="text-6xl animate-pulse">🖥️⚡</div>
                        <div className="space-y-3">
                          <h3 className="text-2xl font-bold text-white bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                            Terminal Ready! Connecting... ⚡
                          </h3>
                          <p className="text-gray-300">Your VM is live, establishing secure connection</p>
                        </div>
                        
                        <div className="bg-gray-800/50 rounded-xl p-5 space-y-4">
                          <Loader2 className="h-8 w-8 animate-spin text-green-400 mx-auto" />
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full">
                                  <span className="text-xs text-white">✓</span>
                                </div>
                                <span className="text-gray-300">VM Instance Running</span>
                              </div>
                              <span className="text-green-400 text-xs">Active</span>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full">
                                  <span className="text-xs text-white">✓</span>
                                </div>
                                <span className="text-gray-300">Storage Devices Ready</span>
                              </div>
                              <span className="text-green-400 text-xs">Ready</span>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />
                                <span className="text-gray-300">Web Terminal Loading...</span>
                              </div>
                              <span className="text-blue-400 text-xs">Loading</span>
                            </div>
                          </div>
                          
                          <div className="border-t border-gray-700 pt-3">
                            <div className="text-center text-xs text-gray-400">
                              🔒 Secure connection establishing to your personal VM
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex justify-center space-x-1">
                          <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Status - Clean */}
        <div className="flex items-center justify-between text-sm text-gray-600 bg-white p-4 border rounded-lg mt-6">
          <div className="flex items-center space-x-4">
            <span>👤 User: student</span>
            <span>🏠 Home: /home/student</span>
            <span>💿 OS: RHEL 9</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${terminalStatus.running ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <span>{terminalStatus.running ? 'Connected' : 'Disconnected'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Labs;
